
import React, { useEffect, useState, useRef } from 'react';
import { PiggyBank, Scale, Cpu, TrendingUp, ArrowRight } from 'lucide-react';
import { ScrollLink } from 'react-scroll';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '../../hooks/use-mobile';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/contexts/LanguageContext';

const WhatWeDoSection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();
  const lineRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const { t, language } = useLanguage();
  
  const handleNavigation = () => {
    navigate('/why-sell-to-noa');
    window.scrollTo(0, 0);
  };
  
  const openContactEmail = () => {
    const subject = encodeURIComponent("Quiero saber más sobre Noa Partners");
    const body = encodeURIComponent("Quiero conocer más sobre cómo mi empresa podría beneficiarse al asociarse con Noa.");
    window.location.href = `mailto:info@noapartners.com?subject=${subject}&body=${body}`;
  };
  
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const sectionTop = sectionRef.current?.getBoundingClientRect().top || 0;
          const sectionBottom = sectionRef.current?.getBoundingClientRect().bottom || 0;
          
          if (sectionTop <= 100 && sectionBottom >= 300) {
            setIsScrolled(true);
          } else {
            setIsScrolled(false);
          }

          if (lineRef.current && sectionRef.current) {
            const sectionHeight = sectionRef.current.offsetHeight;
            const sectionOffset = sectionRef.current.offsetTop;
            const windowHeight = window.innerHeight;
            const scrollPosition = window.scrollY - sectionOffset + windowHeight / 2;
            
            const scrollPercentage = Math.min(Math.max(scrollPosition / sectionHeight * 1.2, 0), 1);
            
            lineRef.current.style.height = `${scrollPercentage * 100}%`;
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  if (location.pathname === '/about') {
    return null;
  }
  
  return (
    <section id="what-we-do" ref={sectionRef} className="py-32 bg-white dark:bg-noa-rich-black relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className={`lg:col-span-5 ${!isMobile && isScrolled ? 'lg:sticky lg:top-24' : ''}`}>
            <h2 className="font-space-grotesk text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-noa-rich-black dark:text-noa-antiflash-white">
              {language === 'es' ? (
                <>
                  Sobre<br />
                  Noa<span className="text-blue-500">.</span>
                </>
              ) : (
                <>
                  About<br />
                  Noa<span className="text-blue-500">.</span>
                </>
              )}
            </h2>
          </div>
          
          <div className="lg:col-span-7 relative">
            <div className="absolute left-0 w-0.5 bg-noa-charcoal/20 dark:bg-noa-antiflash-white/20 h-full z-0">
              <div 
                ref={lineRef} 
                className="absolute top-0 left-0 w-full bg-gradient-to-b from-blue-600 via-blue-500 to-blue-400 dark:from-blue-500 dark:via-blue-400 dark:to-blue-300 rounded-full" 
                style={{ height: '0%' }}
              />
            </div>
            
            <div className="relative pl-8 mb-20">
              <p className="text-xl sm:text-2xl font-light text-noa-charcoal dark:text-noa-antiflash-white/90 mb-6 max-w-2xl leading-relaxed">
                {t('about_description')}
              </p>
            </div>
            
            <div className="relative">
              <h3 className="text-noa-charcoal dark:text-noa-antiflash-white font-medium mb-8 pl-8">
                {t('benefits_of_partnering')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
                <div className="p-6 relative">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 mr-3 flex items-center justify-center rounded-full bg-noa-antiflash-white dark:bg-noa-charcoal">
                      <PiggyBank className="w-5 h-5 text-noa-charcoal dark:text-noa-antiflash-white" />
                    </div>
                    <h3 className="font-space-grotesk text-xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {t('permanent_home')}
                    </h3>
                  </div>
                  <p className="text-noa-charcoal dark:text-noa-antiflash-white/70 leading-relaxed">
                    {t('permanent_home_desc')}
                  </p>
                </div>
                
                <div className="p-6 relative">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 mr-3 flex items-center justify-center rounded-full bg-noa-antiflash-white dark:bg-noa-charcoal">
                      <Cpu className="w-5 h-5 text-noa-charcoal dark:text-noa-antiflash-white" />
                    </div>
                    <h3 className="font-space-grotesk text-xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {t('resources_for_growth')}
                    </h3>
                  </div>
                  <p className="text-noa-charcoal dark:text-noa-antiflash-white/70 leading-relaxed">
                    {t('resources_desc').replace('Noa Platform', t('noa_platform'))}
                  </p>
                </div>
                
                <div className="p-6 relative">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 mr-3 flex items-center justify-center rounded-full bg-noa-antiflash-white dark:bg-noa-charcoal">
                      <TrendingUp className="w-5 h-5 text-noa-charcoal dark:text-noa-antiflash-white" />
                    </div>
                    <h3 className="font-space-grotesk text-xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {t('aligned_incentives')}
                    </h3>
                  </div>
                  <p className="text-noa-charcoal dark:text-noa-antiflash-white/70 leading-relaxed">
                    {t('aligned_incentives_desc')}
                  </p>
                </div>
                
                <div className="p-6 relative">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 mr-3 flex items-center justify-center rounded-full bg-noa-antiflash-white dark:bg-noa-charcoal">
                      <Scale className="w-5 h-5 text-noa-charcoal dark:text-noa-antiflash-white" />
                    </div>
                    <h3 className="font-space-grotesk text-xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {t('autonomy')}
                    </h3>
                  </div>
                  <p className="text-noa-charcoal dark:text-noa-antiflash-white/70 leading-relaxed">
                    {t('autonomy_desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="py-16 bg-white dark:bg-noa-rich-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="bg-[#1A1F2C] dark:bg-[#221F26] rounded-lg p-8 md:p-12">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-8 md:mb-0 md:max-w-xl">
                <h3 className="font-space-grotesk text-3xl font-bold text-white dark:text-noa-antiflash-white mb-4">
                  {t('why_partner')}
                </h3>
                <p className="text-white/80 dark:text-noa-antiflash-white/80 text-lg leading-relaxed mb-6">
                  {t('why_partner_desc')}
                </p>
                <Button 
                  onClick={handleNavigation}
                  className="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-700 dark:hover:bg-blue-600 px-6 py-6 rounded-md text-lg flex items-center gap-2"
                >
                  {t('learn_more')}
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="hidden md:block w-64 h-64 bg-[#2F4550]/40 dark:bg-[#403E43]/40 rounded-full relative overflow-hidden">
                <div className="absolute w-48 h-48 bg-[#403E43]/60 dark:bg-[#0EA5E9]/30 rounded-full -bottom-10 -right-10"></div>
                <div className="absolute w-32 h-32 bg-[#586F7C]/60 dark:bg-[#33C3F0]/30 rounded-full top-8 left-6"></div>
                <div className="absolute w-16 h-16 bg-[#33C3F0]/80 dark:bg-[#0EA5E9]/40 rounded-full top-16 right-12"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
